import { ALLOCATION_STATUS_METADATA } from '../constants/allocationStatus'
import { buildCapabilityFilterValues } from '@/features/Collaborators/utils/resourcePresentation'
import { calculateConsolidatedStatus, normalizeStatus } from '@/utils/collaborator-status'
import { resolveSummarySituation } from '@/features/Vacations/utils/vacationSummaryColumns'
import type { FilterConfig, FilterOperator } from '@/types/filters'

const booleanOptions = [
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Não' }
]

const statusOptions = [
  { value: 'PENDING', label: 'Pendente' },
  { value: 'IN_PROGRESS', label: 'Em andamento' },
  { value: 'UPDATED', label: 'Atualizado' }
]

const educationLevelOptions = [
  { value: 'Ensino Fundamental', label: 'Ensino Fundamental' },
  { value: 'Ensino Médio', label: 'Ensino Médio' },
  { value: 'Técnico', label: 'Técnico' },
  { value: 'Tecnólogo', label: 'Tecnólogo' },
  { value: 'Graduação', label: 'Graduação' },
  { value: 'Pós-graduação', label: 'Pós-graduação' },
  { value: 'MBA', label: 'MBA' },
  { value: 'Mestrado', label: 'Mestrado' },
  { value: 'Doutorado', label: 'Doutorado' },
  { value: 'Pós-doutorado', label: 'Pós-doutorado' }
]

const educationLevelRank: Record<string, number> = {
  'Ensino Fundamental': 1,
  'Ensino Médio': 2,
  Técnico: 3,
  Tecnólogo: 4,
  Graduação: 5,
  'Pós-graduação': 6,
  MBA: 7,
  Mestrado: 8,
  Doutorado: 9,
  'Pós-doutorado': 10
}

const numberOperators: FilterOperator[] = [
  'equals',
  'greaterThan',
  'lessThan',
  'greaterThanOrEqual',
  'lessThanOrEqual',
  'between',
  'isEmpty',
  'isNotEmpty'
]

const dateOperators: FilterOperator[] = [
  'equals',
  'greaterThan',
  'lessThan',
  'between',
  'isEmpty',
  'isNotEmpty'
]

const selectOperators: FilterOperator[] = ['equals', 'in', 'notIn', 'isEmpty', 'isNotEmpty']
const textOperators: FilterOperator[] = ['contains', 'startsWith', 'endsWith', 'equals', 'isEmpty', 'isNotEmpty']
const booleanOperators: FilterOperator[] = ['equals', 'notEquals', 'isEmpty', 'isNotEmpty']

const getSalaryEntries = (item: any) =>
  Array.isArray(item.salaryHistorySalaries) ? item.salaryHistorySalaries : []

const getSalaryDate = (salary: any) => {
  if (!salary?.date) return null
  const date = new Date(salary.date)
  return Number.isNaN(date.getTime()) ? null : date
}

const getSalaryAmount = (salary: any) => {
  const amount = Number(salary?.amount)
  return Number.isFinite(amount) ? amount : null
}

const getSortedSalaries = (item: any) =>
  getSalaryEntries(item)
    .filter((salary: any) => getSalaryDate(salary) && getSalaryAmount(salary) !== null)
    .sort((a: any, b: any) => getSalaryDate(a)!.getTime() - getSalaryDate(b)!.getTime())

const getPastSalaries = (item: any) => {
  const now = Date.now()

  return getSortedSalaries(item).filter((salary: any) => getSalaryDate(salary)!.getTime() <= now)
}

const getLatestSalaryEntry = (item: any) => {
  const salaries = [...getSortedSalaries(item)].sort((a: any, b: any) =>
    getSalaryDate(b)!.getTime() - getSalaryDate(a)!.getTime()
  )

  return salaries[0] ?? null
}

const getPreviousSalaryEntry = (item: any) => {
  const salaries = [...getSortedSalaries(item)].sort((a: any, b: any) =>
    getSalaryDate(b)!.getTime() - getSalaryDate(a)!.getTime()
  )

  return salaries[1] ?? null
}

const getLatestSalary = (item: any) => {
  const pastSalaries = getPastSalaries(item)
  return pastSalaries.length > 0 ? pastSalaries[pastSalaries.length - 1] : null
}

const getNextPlannedSalary = (item: any) => {
  const now = Date.now()

  return getSortedSalaries(item).find((salary: any) => getSalaryDate(salary)!.getTime() > now) ?? null
}

const getSalaryProgressionAmount = (item: any) => {
  const salaries = getSortedSalaries(item)

  if (salaries.length < 2) return null

  const firstAmount = getSalaryAmount(salaries[0])
  const lastAmount = getSalaryAmount(salaries[salaries.length - 1])

  if (firstAmount === null || lastAmount === null) return null

  return lastAmount - firstAmount
}

const getLastRaisePercentage = (item: any) => {
  const latestSalary = getLatestSalaryEntry(item)
  const previousSalary = getPreviousSalaryEntry(item)

  if (!latestSalary || !previousSalary) return null

  const latestAmount = getSalaryAmount(latestSalary)
  const previousAmount = getSalaryAmount(previousSalary)

  if (latestAmount === null || previousAmount === null || previousAmount === 0) return null

  return ((latestAmount - previousAmount) / previousAmount) * 100
}

const getTotalSalaryEvolution = (item: any) => {
  const salaries = getSortedSalaries(item)

  if (salaries.length < 2) return null

  const firstAmount = getSalaryAmount(salaries[0])
  const lastAmount = getSalaryAmount(salaries[salaries.length - 1])

  if (firstAmount === null || lastAmount === null || firstAmount === 0) return null

  return ((lastAmount - firstAmount) / firstAmount) * 100
}

const isCurrentlyOnVacation = (item: any) => {
  return resolveSummarySituation(item.vacationSummary).kind === 'onVacation'
}

const getVacationBalance = (item: any) => {
  return item.vacationSummary?.balanceDays ?? null
}

const getVacationEntitledFrom = (item: any) => {
  return item.vacationSummary?.concessivePeriod?.start ?? null
}

const getVacationConcessiveEnd = (item: any) => {
  return item.vacationSummary?.concessivePeriod?.end ?? null
}

const getVacationSituation = (item: any) => {
  return resolveSummarySituation(item.vacationSummary).kind
}

const getAddressByType = (item: any, type: string) =>
  Array.isArray(item.addresses)
    ? item.addresses.find((address: any) => address?.type === type)
    : null

const formatAddress = (address: any) => {
  if (!address) return null

  return [
    address.street,
    address.number,
    address.complement,
    address.neighborhood,
    address.city,
    address.state,
    address.zipCode
  ]
    .filter(Boolean)
    .join(', ') || null
}

const getHighestEducation = (item: any) => {
  const educations = Array.isArray(item.educations) ? item.educations : []

  return educations
    .map((education: any) => education?.level)
    .filter(Boolean)
    .sort((a: string, b: string) => (educationLevelRank[b] || 0) - (educationLevelRank[a] || 0))[0] ?? null
}

const getMainEducationInstitution = (item: any) => {
  const educations = Array.isArray(item.educations) ? item.educations : []

  return educations
    .map((education: any) => education?.institution)
    .filter(Boolean)[0] ?? null
}

const getMedicalCertificates = (item: any) =>
  Array.isArray(item.medicalCertificates) ? item.medicalCertificates : []

const getLastMedicalCertificateDate = (item: any) => {
  const lastCertificateDate = getMedicalCertificates(item)
    .map((certificate: any) => certificate?.date)
    .filter(Boolean)
    .map((date: string) => new Date(date))
    .filter((date: Date) => !Number.isNaN(date.getTime()))
    .sort((a: Date, b: Date) => b.getTime() - a.getTime())[0]

  return lastCertificateDate?.toISOString() ?? null
}

const getTotalMedicalDaysOff = (item: any) =>
  getMedicalCertificates(item).reduce((total: number, certificate: any) => {
    const days = Number(certificate?.daysOff)
    return total + (Number.isFinite(days) ? days : 0)
  }, 0)

const getArrayCount = (value: unknown) => (Array.isArray(value) ? value.length : 0)

const getActiveAllocationNames = (item: any) => {
  const allocations = Array.isArray(item.allocationPreview?.activeAllocations)
    ? item.allocationPreview.activeAllocations
    : []

  return allocations
    .map((allocation: any) =>
      allocation?.clientName ||
      allocation?.client?.name ||
      allocation?.projectName ||
      allocation?.name ||
      allocation?.allocationType
    )
    .filter(Boolean)
}

export const DISPLAY_COLUMN_FILTER_CONFIGS: FilterConfig[] = [
  {
    columnId: 'consolidatedStatus',
    label: 'Status Consolidado',
    type: 'select',
    category: 'Status',
    operators: selectOperators,
    options: statusOptions
  },
  {
    columnId: 'dhoStatus',
    label: 'Status DHO',
    type: 'select',
    category: 'Status',
    operators: selectOperators,
    options: statusOptions
  },
  {
    columnId: 'dpStatus',
    label: 'Status DP',
    type: 'select',
    category: 'Status',
    operators: selectOperators,
    options: statusOptions
  },
  {
    columnId: 'techStatus',
    label: 'Status Tech',
    type: 'select',
    category: 'Status',
    operators: selectOperators,
    options: statusOptions
  },
  {
    columnId: 'userStatus',
    label: 'Status User',
    type: 'select',
    category: 'Status',
    operators: selectOperators,
    options: statusOptions
  },
  {
    columnId: 'allocationStatus',
    label: 'Status de Alocação',
    type: 'select',
    category: 'Alocação',
    operators: selectOperators,
    options: Object.entries(ALLOCATION_STATUS_METADATA).map(([value, metadata]) => ({
      value,
      label: metadata.title
    }))
  },
  {
    columnId: 'allocationSummary',
    label: 'Alocações Ativas',
    type: 'multiselect',
    category: 'Alocação',
    operators: ['in', 'notIn', 'isEmpty', 'isNotEmpty'],
    options: []
  },
  {
    columnId: 'isRaroAcademyAlumnus',
    label: 'Ex-Raro Academy',
    type: 'boolean',
    category: 'Dados Específicos',
    operators: booleanOperators,
    options: booleanOptions
  },
  {
    columnId: 'internshipEducationalInstitution',
    label: 'Instituição de Ensino',
    type: 'select',
    category: 'Dados de Estagiário',
    operators: selectOperators,
    options: []
  },
  {
    columnId: 'internshipCourse',
    label: 'Curso',
    type: 'select',
    category: 'Dados de Estagiário',
    operators: selectOperators,
    options: []
  },
  {
    columnId: 'internshipEndDate',
    label: 'Data de Término do Estágio',
    type: 'date',
    category: 'Dados de Estagiário',
    operators: dateOperators
  },
  {
    columnId: 'isPCD',
    label: 'É PCD',
    type: 'boolean',
    category: 'Informações de Saúde',
    operators: booleanOperators,
    options: booleanOptions
  },
  {
    columnId: 'shirtSize',
    label: 'Tamanho da Camisa',
    type: 'select',
    category: 'Preferências Pessoais',
    operators: selectOperators,
    options: [
      { value: 'PP', label: 'PP' },
      { value: 'P', label: 'P' },
      { value: 'M', label: 'M' },
      { value: 'G', label: 'G' },
      { value: 'GG', label: 'GG' },
      { value: 'XG', label: 'XG' },
      { value: 'XGG', label: 'XGG' }
    ]
  },
  {
    columnId: 'hasOnboardingKit',
    label: 'Kit de Onboarding',
    type: 'boolean',
    category: 'Preferências Pessoais',
    operators: booleanOperators,
    options: booleanOptions
  },
  {
    columnId: 'experienceEndDate',
    label: 'Data de Fim da Experiência',
    type: 'date',
    category: 'Dados Contratuais Finais',
    operators: dateOperators
  },
  {
    columnId: 'raroFeedbacksCount',
    label: 'Feedbacks Internos',
    type: 'number',
    category: 'Contadores',
    operators: numberOperators
  },
  {
    columnId: 'clientFeedbacksCount',
    label: 'Feedbacks de Cliente',
    type: 'number',
    category: 'Contadores',
    operators: numberOperators
  },
  {
    columnId: 'occurrencesCount',
    label: 'Ocorrências',
    type: 'number',
    category: 'Contadores',
    operators: numberOperators
  },
  {
    columnId: 'childrenCount',
    label: 'Filhos',
    type: 'number',
    category: 'Contadores',
    operators: numberOperators
  },
  {
    columnId: 'healthDependentsCount',
    label: 'Dependentes do Plano',
    type: 'number',
    category: 'Contadores',
    operators: numberOperators
  },
  {
    columnId: 'mainAddress',
    label: 'Endereço Principal',
    type: 'text',
    category: 'Endereços',
    operators: textOperators
  },
  {
    columnId: 'mailingAddress',
    label: 'Endereço de Correspondência',
    type: 'text',
    category: 'Endereços',
    operators: textOperators
  },
  {
    columnId: 'salaryProgression',
    label: 'Progressão Salarial',
    type: 'number',
    category: 'Métricas Salariais',
    operators: numberOperators
  },
  {
    columnId: 'lastRaisePercentage',
    label: '% do Último Aumento',
    type: 'number',
    category: 'Métricas Salariais',
    operators: numberOperators
  },
  {
    columnId: 'totalSalaryEvolution',
    label: '% Evolução Salarial',
    type: 'number',
    category: 'Métricas Salariais',
    operators: numberOperators
  },
  {
    columnId: 'lastExtraAmount',
    label: 'Último Extra/Caju',
    type: 'number',
    category: 'Métricas Salariais',
    operators: numberOperators
  },
  {
    columnId: 'lastSalaryReason',
    label: 'Motivo do Último Salário',
    type: 'select',
    category: 'Métricas Salariais',
    operators: selectOperators,
    options: []
  },
  {
    columnId: 'lastSalaryObservations',
    label: 'Observações do Último Salário',
    type: 'text',
    category: 'Métricas Salariais',
    operators: textOperators
  },
  {
    columnId: 'lastSalaryWithIncrease',
    label: 'Último Salário + % Aumento',
    type: 'number',
    category: 'Métricas Salariais',
    operators: numberOperators
  },
  {
    columnId: 'salaryProgressionComposite',
    label: 'Progressão Completa',
    type: 'number',
    category: 'Métricas Salariais',
    operators: numberOperators
  },
  {
    columnId: 'plannedSalaryIncrease',
    label: 'Aumento Planejado',
    type: 'number',
    category: 'Métricas Salariais',
    operators: numberOperators
  },
  {
    columnId: 'isCurrentlyOnVacation',
    label: 'Está de férias no momento',
    type: 'boolean',
    category: 'Gestão de Férias',
    operators: booleanOperators,
    options: booleanOptions
  },
  {
    columnId: 'vacationBalance',
    label: 'Saldo de Dias',
    type: 'number',
    category: 'Gestão de Férias',
    operators: numberOperators
  },
  {
    columnId: 'vacationEntitledFrom',
    label: 'Direito a partir de',
    type: 'date',
    category: 'Gestão de Férias',
    operators: dateOperators
  },
  {
    columnId: 'vacationConcessivePeriod',
    label: 'Limite para agendar',
    type: 'date',
    category: 'Gestão de Férias',
    operators: dateOperators
  },
  {
    columnId: 'vacationSituation',
    label: 'Situação de Férias',
    type: 'select',
    category: 'Gestão de Férias',
    operators: selectOperators,
    options: [
      { value: 'notCalculated', label: 'Não calculado' },
      { value: 'notApplicable', label: 'Não se aplica (PJ, inativo...)' },
      { value: 'notEntitledYet', label: 'Ainda sem direito' },
      { value: 'onVacation', label: 'Em férias' },
      { value: 'scheduled', label: 'Agendada' },
      { value: 'pendingApproval', label: 'Aguardando aprovação' },
      { value: 'expired', label: 'Férias vencidas' },
      { value: 'noScheduling', label: 'Sem agendamento' },
    ]
  },
  {
    columnId: 'highestEducationLevel',
    label: 'Maior Nível de Formação',
    type: 'select',
    category: 'Formação Acadêmica',
    operators: selectOperators,
    options: educationLevelOptions
  },
  {
    columnId: 'mainEducationInstitution',
    label: 'Instituição Principal',
    type: 'select',
    category: 'Formação Acadêmica',
    operators: selectOperators,
    options: []
  },
  {
    columnId: 'lastMedicalCertificate',
    label: 'Último Atestado Médico',
    type: 'date',
    category: 'Informações de Saúde',
    operators: dateOperators
  },
  {
    columnId: 'totalMedicalDaysOff',
    label: 'Total de Dias Afastados',
    type: 'number',
    category: 'Informações de Saúde',
    operators: numberOperators
  },
  {
    columnId: 'primarySkill',
    label: 'Habilidades / Capacidades Técnicas',
    type: 'multiselect',
    category: 'Recursos Técnicos',
    operators: ['in', 'notIn', 'isEmpty', 'isNotEmpty'],
    options: []
  }
]

export const DISPLAY_COLUMN_FIELD_ACCESSORS = {
  consolidatedStatus: (item: any) => calculateConsolidatedStatus(item),
  dhoStatus: (item: any) => normalizeStatus(item.updatedStatus?.byDHO?.status),
  dpStatus: (item: any) => normalizeStatus(item.updatedStatus?.byDP?.status),
  techStatus: (item: any) => normalizeStatus(item.updatedStatus?.byTech?.status),
  userStatus: (item: any) => normalizeStatus(item.updatedStatus?.byUser?.status),
  allocationStatus: (item: any) => item.allocationPreview?.status || null,
  allocationSummary: getActiveAllocationNames,
  isRaroAcademyAlumnus: (item: any) => item.isRaroAcademyAlumnus,
  internshipEducationalInstitution: (item: any) => item.internshipEducationalInstitution || null,
  internshipCourse: (item: any) => item.internshipCourse || null,
  internshipEndDate: (item: any) => item.internshipEndDate || null,
  isPCD: (item: any) => item.isPCD,
  shirtSize: (item: any) => item.shirtSize || null,
  hasOnboardingKit: (item: any) => item.hasOnboardingKit,
  experienceEndDate: (item: any) => item.experienceEndDate || null,
  raroFeedbacksCount: (item: any) => getArrayCount(item.raroFeedbacks),
  clientFeedbacksCount: (item: any) => getArrayCount(item.clientFeedbacks),
  occurrencesCount: (item: any) => getArrayCount(item.occurrences),
  childrenCount: (item: any) => getArrayCount(item.children),
  healthDependentsCount: (item: any) => getArrayCount(item.healthDependents),
  mainAddress: (item: any) => formatAddress(getAddressByType(item, 'main')),
  mailingAddress: (item: any) => formatAddress(getAddressByType(item, 'mailing')),
  salaryProgression: getSalaryProgressionAmount,
  lastRaisePercentage: getLastRaisePercentage,
  totalSalaryEvolution: getTotalSalaryEvolution,
  lastExtraAmount: (item: any) => {
    const latestSalary = getLatestSalaryEntry(item)
    if (!latestSalary) return null

    const extraAmount = Number(latestSalary.extras)
    return Number.isFinite(extraAmount) && extraAmount > 0 ? extraAmount : null
  },
  lastSalaryReason: (item: any) => getLatestSalaryEntry(item)?.reason || null,
  lastSalaryObservations: (item: any) => getLatestSalaryEntry(item)?.observations || null,
  lastSalaryWithIncrease: (item: any) => getSalaryAmount(getLatestSalary(item)),
  salaryProgressionComposite: getSalaryProgressionAmount,
  plannedSalaryIncrease: (item: any) => getSalaryAmount(getNextPlannedSalary(item)),
  isCurrentlyOnVacation,
  vacationBalance: getVacationBalance,
  vacationEntitledFrom: getVacationEntitledFrom,
  vacationConcessivePeriod: getVacationConcessiveEnd,
  vacationSituation: getVacationSituation,
  highestEducationLevel: getHighestEducation,
  mainEducationInstitution: getMainEducationInstitution,
  lastMedicalCertificate: getLastMedicalCertificateDate,
  totalMedicalDaysOff: getTotalMedicalDaysOff,
  primarySkill: (item: any) => buildCapabilityFilterValues(item.resources)
}
